export type RoutePageKind =
    | 'admin'
    | 'billing'
    | 'auth'
    | 'oauth-callback'
    | 'oauth-client-return'
    | 'mcp-oauth-callback'
    | 'oauth-success'
    | 'onboarding'
    | 'owner-transfer-accept'
    | 'payment-return'
    | 'public';

export interface RouteBehavior {
    requiresAuth: boolean;
    usesAdminLayout: boolean;
    deferGlobalLoadingToLayout: boolean;
    allowAnonymous: boolean;
    pageKind: RoutePageKind;
    suppressAuthGlobalLoading: boolean;
}

const ADMIN_BASENAME = '/admin';

const normalizeRoutePath = (pathname: string): string => {
    const [withoutHash] = pathname.split('#');
    const [withoutSearch] = withoutHash.split('?');
    const normalizedPathname = withoutSearch || '/';

    if (normalizedPathname === ADMIN_BASENAME) {
        return '/';
    }

    if (normalizedPathname.startsWith(`${ADMIN_BASENAME}/`)) {
        return normalizedPathname.slice(ADMIN_BASENAME.length) || '/';
    }

    return normalizedPathname || '/';
};

const buildRouteBehavior = (behavior: RouteBehavior): RouteBehavior => behavior;

export const getRouteBehavior = (pathname: string): RouteBehavior => {
    const routePath = normalizeRoutePath(pathname);

    if (routePath === '/auth' || routePath.startsWith('/auth/')) {
        return buildRouteBehavior({
            requiresAuth: false,
            usesAdminLayout: false,
            deferGlobalLoadingToLayout: false,
            allowAnonymous: true,
            pageKind: 'auth',
            suppressAuthGlobalLoading: false,
        });
    }

    if (routePath === '/oauth/success' || routePath.startsWith('/oauth/success/')) {
        return buildRouteBehavior({
            requiresAuth: true,
            usesAdminLayout: false,
            deferGlobalLoadingToLayout: false,
            allowAnonymous: false,
            pageKind: 'oauth-success',
            suppressAuthGlobalLoading: false,
        });
    }

    if (routePath === '/oauth/client-return' || routePath.startsWith('/oauth/client-return/')) {
        return buildRouteBehavior({
            requiresAuth: true,
            usesAdminLayout: false,
            deferGlobalLoadingToLayout: false,
            allowAnonymous: false,
            pageKind: 'oauth-client-return',
            suppressAuthGlobalLoading: true,
        });
    }

    if (routePath === '/judgeme/oauth/callback') {
        return buildRouteBehavior({
            requiresAuth: true,
            usesAdminLayout: false,
            deferGlobalLoadingToLayout: false,
            allowAnonymous: false,
            pageKind: 'oauth-callback',
            suppressAuthGlobalLoading: false,
        });
    }

    if (routePath === '/mcp/oauth/callback') {
        return buildRouteBehavior({
            requiresAuth: false,
            usesAdminLayout: false,
            deferGlobalLoadingToLayout: false,
            allowAnonymous: true,
            pageKind: 'mcp-oauth-callback',
            suppressAuthGlobalLoading: true,
        });
    }

    if (routePath === '/onboarding' || routePath.startsWith('/onboarding/')) {
        return buildRouteBehavior({
            requiresAuth: true,
            usesAdminLayout: false,
            deferGlobalLoadingToLayout: false,
            allowAnonymous: false,
            pageKind: 'onboarding',
            suppressAuthGlobalLoading: false,
        });
    }

    if (routePath === '/payment/return' || routePath.startsWith('/payment/return/')) {
        return buildRouteBehavior({
            requiresAuth: true,
            usesAdminLayout: false,
            deferGlobalLoadingToLayout: false,
            allowAnonymous: false,
            pageKind: 'payment-return',
            suppressAuthGlobalLoading: true,
        });
    }

    if (routePath === '/owner-transfer/accept') {
        return buildRouteBehavior({
            requiresAuth: false,
            usesAdminLayout: false,
            deferGlobalLoadingToLayout: false,
            allowAnonymous: true,
            pageKind: 'owner-transfer-accept',
            suppressAuthGlobalLoading: false,
        });
    }

    if (routePath === '/billing' || routePath.startsWith('/billing/')) {
        return buildRouteBehavior({
            requiresAuth: true,
            usesAdminLayout: true,
            deferGlobalLoadingToLayout: false,
            allowAnonymous: false,
            pageKind: 'billing',
            suppressAuthGlobalLoading: true,
        });
    }

    if (routePath === '/invite' || routePath.startsWith('/invite/')) {
        return buildRouteBehavior({
            requiresAuth: false,
            usesAdminLayout: false,
            deferGlobalLoadingToLayout: false,
            allowAnonymous: true,
            pageKind: 'public',
            suppressAuthGlobalLoading: false,
        });
    }

    return buildRouteBehavior({
        requiresAuth: true,
        usesAdminLayout: true,
        deferGlobalLoadingToLayout: true,
        allowAnonymous: false,
        pageKind: 'admin',
        suppressAuthGlobalLoading: false,
    });
};

export const isAdminLayoutGlobalLoadingOwner = (pathname: string): boolean => {
    const behavior = getRouteBehavior(pathname);
    return behavior.usesAdminLayout
        && behavior.deferGlobalLoadingToLayout
        && !behavior.suppressAuthGlobalLoading;
};
